import React, { useEffect, useState } from 'react';
import { Sparkles, Save, Send, Globe, Instagram, Facebook, ExternalLink, RefreshCw, ImagePlus, X, Camera, Upload, Link as LinkIcon, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  { key: 'pending_review', label: 'Submitted', color: 'bg-stone-100 text-stone-700' },
  { key: 'in_curation', label: 'In Curation', color: 'bg-amber-100 text-amber-800' },
  { key: 'ai_drafted', label: 'AI Drafted', color: 'bg-blue-100 text-blue-800' },
  { key: 'active', label: 'Live', color: 'bg-emerald-100 text-emerald-800' },
];

const SOCIAL_FIELDS = [
  { key: 'website', label: 'Website', icon: Globe },
  { key: 'instagram', label: 'Instagram', icon: Instagram },
  { key: 'facebook', label: 'Facebook', icon: Facebook },
  { key: 'airbnb', label: 'Airbnb', icon: ExternalLink },
  { key: 'booking', label: 'Booking.com', icon: ExternalLink },
  { key: 'mmt', label: 'MakeMyTrip', icon: ExternalLink },
  { key: 'tripadvisor', label: 'TripAdvisor', icon: ExternalLink },
  { key: 'google_maps', label: 'Google Maps', icon: ExternalLink },
  { key: 'youtube', label: 'YouTube', icon: ExternalLink },
];

// Canonical sections the live stay page renders. Keep in sync with
// SECTION_LABELS in FeaturedStay.js / CollectiveStay.js.
const PHOTO_SECTIONS = [
  { key: 'hero',       label: 'Hero',       hint: 'Top of page · 1 photo' },
  { key: 'property',   label: 'Property',   hint: 'Building exterior / approach' },
  { key: 'rooms',      label: 'Rooms',      hint: 'Bedrooms & interiors' },
  { key: 'common',     label: 'Common',     hint: 'Lounge, library, common spaces' },
  { key: 'dining',     label: 'Dining',     hint: 'Food, kitchen, dining table' },
  { key: 'view',       label: 'View',       hint: 'What you see from the stay' },
  { key: 'landscape',  label: 'Landscape',  hint: 'Wider valley · nature' },
  { key: 'experience', label: 'Experience', hint: 'Activities · journeys · culture' },
];
const SECTION_KEYS = PHOTO_SECTIONS.map(s => s.key);

// Photo tile — draggable thumbnail. Module-scope to avoid the unstable-nested-
// component lint error and to keep render cheap.
function PhotoTile({ photo, sectionKey, onUnassign }) {
  const fullUrl = photo.url.startsWith('http') ? photo.url : `${API}${photo.url}`;
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('photo/url', photo.url); e.dataTransfer.effectAllowed = 'move'; }}
      className="relative aspect-square w-20 h-20 rounded overflow-hidden border-2 border-transparent hover:border-stone-400 cursor-grab active:cursor-grabbing flex-shrink-0"
      data-testid={`tile-${photo.url.slice(-20)}`}
      title={photo.source || 'host'}
    >
      <img src={fullUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = '0.3'; }} />
      {sectionKey && (
        <button
          onClick={() => onUnassign(photo.url)}
          className="absolute top-0.5 right-0.5 bg-rose-600/90 hover:bg-rose-700 text-white rounded-full p-0.5"
          title="Remove from live set"
        ><X size={10} /></button>
      )}
      {!sectionKey && (
        <span className="absolute bottom-0.5 left-0.5 bg-black/60 text-white text-[8px] px-1 rounded uppercase tracking-wider">{photo.source || 'host'}</span>
      )}
    </div>
  );
}

// Drop zone for one section. Self-contains `over` highlight state.
function SectionDropRow({ sectionKey, label, hint, photos, onAssign, onUnassign }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (!over) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setOver(false);
        const url = e.dataTransfer.getData('photo/url');
        if (url) onAssign(url, sectionKey);
      }}
      className={`border-2 rounded-md p-2 mb-1.5 transition ${over ? 'border-emerald-500 bg-emerald-50' : photos.length ? 'border-stone-200 bg-white' : 'border-dashed border-stone-300 bg-stone-50'}`}
      data-testid={`section-bucket-${sectionKey}`}
    >
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-700">
          {label} <span className="text-stone-400 font-normal normal-case tracking-normal">· {photos.length}</span>
        </span>
        <span className="text-[9px] italic text-stone-500">{hint}</span>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {photos.map(p => <PhotoTile key={p.url} photo={p} sectionKey={sectionKey} onUnassign={onUnassign} />)}
        {photos.length === 0 && <span className="text-[10px] text-stone-400 italic py-2 px-1">drop a photo here</span>}
      </div>
    </div>
  );
}

// Read-only live preview pane — mimics the stay page layout.
// Used by the side-by-side curation view.
function LivePreviewPane({ stay, libraryAll, selectedSet, photoSections, tagline, storyline, onEditSocials }) {
  const buckets = {};
  for (const k of SECTION_KEYS) buckets[k] = [];
  for (const p of libraryAll) {
    if (selectedSet.has(p.url)) {
      const sec = photoSections[p.url] || 'property';
      (buckets[sec] || buckets.property).push(p);
    }
  }
  const heroPhoto = buckets.hero[0] || buckets.property[0] || libraryAll[0];
  const fullUrl = (u) => u && (u.startsWith('http') ? u : `${API}${u}`);
  const nonEmpty = PHOTO_SECTIONS.filter(s => s.key !== 'hero' && buckets[s.key].length > 0);
  const SECTION_LABEL_LIVE = { property: 'The Property', rooms: 'The Rooms', common: 'The Common Spaces', dining: 'Food & Dining', view: 'The View', landscape: 'The Landscape', experience: 'Experience' };

  return (
    <div className="border border-stone-300 rounded-lg overflow-hidden bg-white" data-testid="live-preview-pane">
      <div className="bg-amber-700 text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] flex items-center justify-between">
        <span>Live preview · what visitors will see</span>
        <span className="text-[10px] normal-case tracking-normal opacity-80">live · updates as you drag</span>
      </div>
      <div className="relative aspect-[16/8] bg-stone-200">
        {heroPhoto ? (
          <img src={fullUrl(heroPhoto.url)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs px-4 text-center">No hero photo yet — drop one in the Hero bucket on the left</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-3 left-4 right-4">
          <div className="text-[9px] uppercase tracking-[0.3em] text-white/80">{stay?.valley || ''}</div>
          <div className="text-xl text-white italic font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{stay?.display_name?.replace(/,.*$/, '') || 'Stay name'}</div>
        </div>
      </div>
      {tagline && <div className="px-4 py-3 text-center text-sm italic text-stone-700 border-b border-stone-100">{tagline}</div>}
      {nonEmpty.length === 0 ? (
        <div className="p-6 text-center text-xs text-stone-500">No section photos placed yet. Drop photos into <b>Property</b>, <b>Rooms</b>, etc. to populate this preview.</div>
      ) : (
        <div className="divide-y divide-stone-100 max-h-[40vh] overflow-y-auto">
          {nonEmpty.map(sec => (
            <div key={sec.key} className="px-4 py-3">
              <div className="text-[9px] uppercase tracking-[0.3em] text-amber-900/60 mb-2">{SECTION_LABEL_LIVE[sec.key] || sec.label}</div>
              <div className={`grid gap-1.5 ${buckets[sec.key].length === 1 ? 'grid-cols-1' : buckets[sec.key].length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {buckets[sec.key].map(p => (
                  <div key={p.url} className="aspect-[4/3] overflow-hidden rounded">
                    <img src={fullUrl(p.url)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = '0.3'; }} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {storyline && (
        <div className="px-4 py-3 border-t border-stone-100 text-xs text-stone-700 leading-relaxed whitespace-pre-line max-h-[16vh] overflow-y-auto">
          {storyline.length > 600 ? storyline.slice(0, 600).trim() + '…' : storyline}
        </div>
      )}
      {onEditSocials && (
        <button
          onClick={onEditSocials}
          className="w-full text-[11px] px-4 py-2 border-t border-stone-200 bg-stone-50 text-stone-700 hover:bg-stone-100 inline-flex items-center justify-center gap-1.5"
          data-testid="preview-edit-socials-btn"
        >
          <LinkIcon size={11} /> Edit social links + run scrape
        </button>
      )}
    </div>
  );
}

/**
 * Inline curation editor — opens on the right pane of /admin/review when a stay is selected.
 * Receives the stay's display_name + admin password from the parent.
 * No own auth, no own routing — just a panel.
 */
export default function CurationPanel({ displayName, password, onClose, onUpdated }) {
  const [stay, setStay] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  const [tagline, setTagline] = useState('');
  const [storyline, setStoryline] = useState('');
  const [bestFor, setBestFor] = useState('');
  const [expTypes, setExpTypes] = useState('');
  const [socialLinks, setSocialLinks] = useState({});
  const [selectedSet, setSelectedSet] = useState(new Set());
  const [photoSections, setPhotoSections] = useState({});

  // Stay Vitals inputs — drive the dramatic strip on the live page
  const [unitCount, setUnitCount] = useState('');
  const [unitType, setUnitType] = useState('');
  const [sleepsMax, setSleepsMax] = useState('');
  const [startingPrice, setStartingPrice] = useState('');
  const [priceIncludes, setPriceIncludes] = useState('');
  const [amenities, setAmenities] = useState('');
  const [partnershipStatus, setPartnershipStatus] = useState('discovered');
  const [whatsappNumber, setWhatsappNumber] = useState('');

  // ── Host & contact (so support can fill what hosts left blank on /apply) ──
  const [hostName, setHostName] = useState('');
  const [hostEmail, setHostEmail] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [altitude, setAltitude] = useState('');
  const [bookingSourceUrl, setBookingSourceUrl] = useState('');

  // ── Pricing extras ──
  const [priceUnit, setPriceUnit] = useState('per_room');
  const [includesBreakfast, setIncludesBreakfast] = useState(true);

  // ── Inventory breakdown ──
  const [invRooms, setInvRooms] = useState('');
  const [invCottages, setInvCottages] = useState('');
  const [invFamilyRooms, setInvFamilyRooms] = useState('');
  const [invSuites, setInvSuites] = useState('');
  const [invTents, setInvTents] = useState('');

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2200); };

  const fetchStay = async () => {
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/curation/${encodeURIComponent(displayName)}?password=${encodeURIComponent(password)}`);
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to load');
      const d = (await r.json()).stay;
      setStay(d);
      setTagline(d.tagline || '');
      setStoryline(d.storyline || d.description || '');
      setBestFor((d.best_for || []).join(', '));
      setExpTypes((d.experience_types || []).join(', '));
      setSocialLinks(d.social_links || {});
      setUnitCount(d.unit_count ?? '');
      setUnitType(d.unit_type || '');
      setSleepsMax(d.sleeps_max ?? '');
      setStartingPrice(d.starting_price_inr ?? '');
      setPriceIncludes(d.price_includes || '');
      setAmenities((d.amenities || []).join(', '));
      setPartnershipStatus(d.partnership_status || 'discovered');
      setWhatsappNumber(d.whatsapp_number || '');
      // Host & contact
      setHostName(d.host_name || '');
      setHostEmail(d.host_email || '');
      setPropertyType((d.property_type || '').toLowerCase());
      setAltitude(d.altitude_meters ?? d.altitude_m ?? '');
      setBookingSourceUrl(d.booking_source_url || '');
      // Pricing extras
      setPriceUnit(d.price_unit || 'per_room');
      setIncludesBreakfast(d.includes_breakfast !== false);  // default true
      // Inventory breakdown
      const inv = d.inventory || {};
      setInvRooms(inv.rooms ?? '');
      setInvCottages(inv.cottages ?? '');
      setInvFamilyRooms(inv.family_rooms ?? '');
      setInvSuites(inv.suites ?? '');
      setInvTents(inv.tents ?? '');
      const sel = new Set();
      const sections = {};
      (d.images || []).forEach(img => {
        if (img.url) {
          sel.add(img.url);
          sections[img.url] = img.section || 'property';
        }
      });
      setSelectedSet(sel);
      setPhotoSections(sections);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { if (displayName && password) fetchStay(); /* eslint-disable-next-line */ }, [displayName, password]);

  const libraryAll = stay
    ? [...(stay.images || []).map(i => ({ url: i.url, source: 'applicant', section: i.section })),
       ...(stay.photo_library || [])]
        .filter((p, i, arr) => p.url && arr.findIndex(x => x.url === p.url) === i)
    : [];

  const saveDraft = async () => {
    setBusy(true); setError('');
    const selected_photos = Array.from(selectedSet).map(url => ({
      url,
      section: photoSections[url] || 'property',
      type: 'curated',
      alt: stay.display_name,
    }));
    // Build the inventory dict — skip empty/zero entries (back-end strips them anyway)
    const inventory = {};
    const invMap = { rooms: invRooms, cottages: invCottages, family_rooms: invFamilyRooms, suites: invSuites, tents: invTents };
    Object.entries(invMap).forEach(([k, v]) => {
      const n = v === '' ? null : parseInt(v, 10);
      if (n !== null && !isNaN(n) && n > 0) inventory[k] = n;
    });
    try {
      const r = await fetch(`${API}/api/admin/curation/edit?password=${encodeURIComponent(password)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: stay.display_name,
          tagline,
          storyline,
          description: storyline,
          best_for: bestFor.split(',').map(s => s.trim()).filter(Boolean),
          experience_types: expTypes.split(',').map(s => s.trim()).filter(Boolean),
          social_links: socialLinks,
          selected_photos,
          unit_count: unitCount === '' ? null : parseInt(unitCount, 10) || 0,
          unit_type: unitType || null,
          sleeps_max: sleepsMax === '' ? null : parseInt(sleepsMax, 10) || 0,
          amenities: amenities.split(',').map(s => s.trim()).filter(Boolean),
          // host + contact
          host_name: hostName.trim() || null,
          host_email: hostEmail.trim() || null,
          whatsapp_number: whatsappNumber.trim() || null,
          property_type: propertyType || null,
          altitude_meters: altitude === '' ? null : parseInt(altitude, 10) || 0,
          booking_source_url: bookingSourceUrl.trim() || null,
          // pricing
          starting_price_inr: startingPrice === '' ? null : parseInt(startingPrice, 10) || 0,
          price_unit: priceUnit,
          includes_breakfast: includesBreakfast,
          inventory,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      // Tier + price_includes still live on set-stay-meta (the legacy meta endpoint)
      const metaParams = new URLSearchParams({ password, display_name: stay.display_name });
      if (priceIncludes !== '') metaParams.set('price_includes', priceIncludes);
      if (partnershipStatus !== '') metaParams.set('partnership_status', partnershipStatus);
      if ([...metaParams.keys()].some(k => k !== 'password' && k !== 'display_name')) {
        await fetch(`${API}/api/admin/curation/set-stay-meta?${metaParams.toString()}`, { method: 'POST' });
      }
      flash('Saved');
      await fetchStay();
      onUpdated?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const scrapeSocials = async () => {
    if (!Object.values(socialLinks).some(v => v)) { setError('Add at least one social link first'); return; }
    setBusy(true); setError('');
    try {
      await fetch(`${API}/api/admin/curation/edit?password=${encodeURIComponent(password)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: stay.display_name, social_links: socialLinks }),
      });
      const r = await fetch(`${API}/api/admin/curation/scrape-socials?password=${encodeURIComponent(password)}&display_name=${encodeURIComponent(stay.display_name)}`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Scrape failed');
      flash(`+${d.photos_added} photos · ${Object.keys(d.social_meta || {}).length} sources`);
      await fetchStay();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    if (!window.confirm('Run AI to compose tagline + storyline + tags?')) return;
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/admin/curation/ai-generate?password=${encodeURIComponent(password)}&display_name=${encodeURIComponent(stay.display_name)}`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'AI failed');
      flash('AI draft generated');
      await fetchStay();
      onUpdated?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const publish = async () => {
    if (selectedSet.size === 0) { setError('Select at least 1 photo before publishing'); return; }
    if (!storyline.trim()) { setError('Storyline cannot be empty'); return; }
    if (!window.confirm(`Publish "${stay.display_name}" live?`)) return;
    await saveDraft();
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/admin/curation/publish?password=${encodeURIComponent(password)}&display_name=${encodeURIComponent(stay.display_name)}&force=true`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Publish failed');
      flash('Published — now live!');
      await fetchStay();
      onUpdated?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // ─── Upload images directly (when scraping fails or for press shots) ───
  const onPickFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true); setError('');
    try {
      const uploadedUrls = [];
      for (const f of files) {
        if (!f.type.startsWith('image/')) { setError(`Skipped non-image: ${f.name}`); continue; }
        if (f.size > 15 * 1024 * 1024) { setError(`${f.name} exceeds 15 MB — skipped`); continue; }
        const fd = new FormData();
        fd.append('password', password);
        fd.append('file', f);
        const r = await fetch(`${API}/api/admin/curation/photos/upload`, { method: 'POST', body: fd });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Upload failed');
        uploadedUrls.push(d.url);
      }
      if (uploadedUrls.length) {
        const r2 = await fetch(`${API}/api/admin/curation/photos/add?password=${encodeURIComponent(password)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ display_name: stay.display_name, urls: uploadedUrls, source: 'admin_upload', auto_select: true }),
        });
        const d2 = await r2.json();
        if (!r2.ok) throw new Error(d2.detail || 'Library add failed');
        flash(`Uploaded ${uploadedUrls.length} · ${d2.auto_selected || 0} auto-selected to live set · library now ${d2.total_in_library}`);
        await fetchStay();
      }
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const [addUrlValue, setAddUrlValue] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [showSocialsModal, setShowSocialsModal] = useState(false);
  // AI Dialogue
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');

  // Load AI chat history when stay opens
  useEffect(() => {
    setAiHistory(stay?.ai_chat || []);
  }, [stay?.gohimalayan_id]);

  const sendAIMessage = async () => {
    const txt = aiInput.trim();
    if (!txt) return;
    setAiBusy(true); setAiError('');
    try {
      const r = await fetch(`${API}/api/admin/curation/ai-chat?password=${encodeURIComponent(password)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: stay.display_name, user_message: txt }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'AI request failed');
      setAiHistory(d.history || []);
      setAiInput('');
    } catch (e) { setAiError(e.message); } finally { setAiBusy(false); }
  };

  const applyProposal = async (proposal) => {
    if (!proposal) return;
    if (proposal.tagline) setTagline(proposal.tagline);
    if (proposal.storyline) setStoryline(proposal.storyline);
    if (proposal.best_for) setBestFor(Array.isArray(proposal.best_for) ? proposal.best_for.join(', ') : proposal.best_for);
    if (proposal.experience_types) setExpTypes(Array.isArray(proposal.experience_types) ? proposal.experience_types.join(', ') : proposal.experience_types);
    // Auto-save so changes persist
    setTimeout(saveDraft, 100);
    flash('Applied AI proposal — saved.');
  };

  const clearAIHistory = async () => {
    if (!window.confirm('Clear this stay\u2019s AI chat history?')) return;
    setAiBusy(true);
    try {
      await fetch(`${API}/api/admin/curation/ai-chat/clear?password=${encodeURIComponent(password)}&display_name=${encodeURIComponent(stay.display_name)}`, { method: 'POST' });
      setAiHistory([]);
    } finally { setAiBusy(false); }
  };
  const addPhotoByUrl = async () => {
    const urls = addUrlValue.split(/[\n,]/).map(u => u.trim()).filter(Boolean);
    if (!urls.length) { setError('Paste at least one image URL'); return; }
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/admin/curation/photos/add?password=${encodeURIComponent(password)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: stay.display_name, urls, source: 'admin', auto_select: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Add failed');
      flash(`+${d.added} photo${d.added === 1 ? '' : 's'} added · ${d.auto_selected || 0} auto-selected to live set`);
      setAddUrlValue('');
      await fetchStay();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!stay) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-stone-500" data-testid="curation-loading">
        {error ? <span className="text-rose-700">{error}</span> : 'Loading…'}
      </div>
    );
  }

  const stage = STAGES.find(s => s.key === stay.curation_status) || STAGES[0];

  // Build the live page URL based on tier (partner → /collective, others → /stay).
  // Slug derivation MUST match `toFeaturedSlug` in FeaturedStay.js — booking_source
  // for scraped tier is the aggregator brand (hushstays/seclude/banjara/itmenaan)
  // and is shared across many stays, so we always derive from display_name.
  const baseSlug = (stay.display_name || '').split(',')[0].trim().toLowerCase()
    .replace(/['']/g, '').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const liveUrl = stay.partnership_status === 'partner'
    ? `/collective/${stay.booking_source || baseSlug}`
    : `/stay/${baseSlug}`;

  return (
    <div className="h-full flex flex-col bg-white" data-testid="curation-panel">
      {/* Sticky header */}
      <header className="border-b border-stone-200 px-5 py-3 flex items-center justify-between gap-3 sticky top-0 bg-white z-10">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] text-stone-500">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${stage.color}`}>{stage.label}</span>
            <span className="truncate">{stay.gohimalayan_id}</span>
          </div>
          <h2 className="text-base font-serif text-stone-900 leading-tight truncate" data-testid="curate-display-name">{stay.display_name}</h2>
          <p className="text-[11px] text-stone-500 truncate">{stay.host_name || '—'} · {stay.valley || stay.valley_slug}</p>
        </div>
        <div className="flex gap-1.5">
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2.5 py-1 text-xs border border-stone-300 rounded hover:bg-stone-50 flex items-center gap-1"
            data-testid="view-live-btn"
            title="Open live page in new tab"
          >
            <ExternalLink size={13} /> View live
          </a>
          {libraryAll.length < 5 && stay.whatsapp_number && (
            <a
              href={`https://wa.me/${(stay.whatsapp_number || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
                `Hi ${stay.host_name || 'there'} — this is GoHimalayan editorial. We're curating "${stay.display_name}" (${stay.gohimalayan_id}). Could you send us 8-12 more photos of the property? Exteriors, rooms, common areas, and the view. WhatsApp the photos to this number whenever convenient. Thank you!`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2.5 py-1 text-xs bg-amber-100 text-amber-900 rounded hover:bg-amber-200 flex items-center gap-1"
              data-testid="request-photos-btn"
              title={`Only ${libraryAll.length} photos — ping host on WhatsApp`}
            >
              <Camera size={13} /> Request photos
            </a>
          )}
          <button onClick={saveDraft} disabled={busy} className="px-2.5 py-1 text-xs border border-stone-300 rounded hover:bg-stone-50 flex items-center gap-1" data-testid="save-btn">
            <Save size={13} /> Save
          </button>
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className={`px-2.5 py-1 text-xs rounded flex items-center gap-1 ${previewMode ? 'bg-amber-700 text-white hover:bg-amber-800' : 'border border-stone-300 hover:bg-stone-50 text-stone-700'}`}
            data-testid="toggle-preview-mode"
            title={previewMode ? 'Hide live preview pane' : 'Show live preview side-by-side'}
          >
            {previewMode ? <EyeOff size={13} /> : <Eye size={13} />} {previewMode ? 'Hide preview' : 'Preview'}
          </button>
          <button onClick={aiGenerate} disabled={busy} className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50" data-testid="ai-generate-btn">
            <Sparkles size={13} /> AI Draft
          </button>
          <button onClick={() => setShowAIChat(!showAIChat)} className={`px-2.5 py-1 text-xs rounded flex items-center gap-1 ${showAIChat ? 'bg-violet-700 text-white' : 'border border-violet-300 text-violet-700 hover:bg-violet-50'}`} data-testid="ai-chat-btn" title="Iterative chat with AI to refine copy">
            <Sparkles size={13} /> AI Chat {aiHistory.length > 0 ? <span className="ml-0.5 bg-white/30 px-1 rounded text-[10px]">{Math.floor(aiHistory.length / 2)}</span> : ''}
          </button>
          <button onClick={publish} disabled={busy} className="px-2.5 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-50" data-testid="publish-btn">
            <Send size={13} /> Publish
          </button>
          <button onClick={onClose} className="ml-1 p-1 text-stone-400 hover:text-stone-900 hover:bg-stone-100 rounded" title="Close">
            <X size={16} />
          </button>
        </div>
      </header>
      {toast && <div className="bg-emerald-50 border-b border-emerald-200 text-[11px] text-emerald-800 px-5 py-1 text-center">{toast}</div>}
      {error && <div className="bg-rose-50 border-b border-rose-200 text-[11px] text-rose-800 px-5 py-1 text-center">{error}</div>}

      {/* Body */}
      <div className={`flex-1 overflow-y-auto px-5 py-4 grid grid-cols-1 gap-5 ${previewMode ? 'xl:grid-cols-[1fr_520px]' : 'xl:grid-cols-[1fr_380px]'}`}>
        {/* Storyline + photos */}
        <div className="space-y-4">
          {/* Host & Contact — first, because CS needs to fill these fast */}
          <section className="bg-sky-50/50 rounded p-4 border border-sky-200" data-testid="curate-host">
            <h3 className="text-[10px] tracking-[0.3em] uppercase text-sky-900/70 mb-3">Host & contact · complete what /apply missed</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Host name</span>
                <input value={hostName} onChange={e => setHostName(e.target.value)} placeholder="Full name" className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm" data-testid="curate-host-name" />
              </label>
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">WhatsApp (digits, with country code)</span>
                <input value={whatsappNumber} onChange={e => setWhatsappNumber(e.target.value)} placeholder="919810131915" className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm font-mono" data-testid="curate-whatsapp" />
              </label>
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Email</span>
                <input value={hostEmail} onChange={e => setHostEmail(e.target.value)} placeholder="host@example.com" className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm" data-testid="curate-host-email" />
              </label>
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Property type</span>
                <select value={propertyType} onChange={e => setPropertyType(e.target.value)} className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm bg-white" data-testid="curate-property-type">
                  <option value="">— select —</option>
                  <option value="homestay">Homestay</option>
                  <option value="cottage">Cottage</option>
                  <option value="lodge">Lodge</option>
                  <option value="villa">Villa</option>
                  <option value="resort">Resort</option>
                  <option value="guesthouse">Guesthouse</option>
                  <option value="retreat">Retreat</option>
                  <option value="hotel">Hotel</option>
                </select>
              </label>
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Altitude (m)</span>
                <input type="number" min="0" max="6000" value={altitude} onChange={e => setAltitude(e.target.value)} placeholder="e.g. 2050" className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm" data-testid="curate-altitude" />
              </label>
              <label className="text-xs sm:col-span-2">
                <span className="block text-stone-500 mb-1">Booking source URL (host&apos;s existing listing &mdash; Airbnb/Booking/own site)</span>
                <input value={bookingSourceUrl} onChange={e => setBookingSourceUrl(e.target.value)} placeholder="https://…" className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm" data-testid="curate-booking-source-url" />
              </label>
            </div>
          </section>

          <section className="bg-stone-50 rounded p-4 border border-stone-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Tagline · Storyline</h3>
              {stay.ai_drafted_at && <span className="text-[10px] text-blue-600">Last AI: {new Date(stay.ai_drafted_at).toLocaleString()}</span>}
            </div>
            <input value={tagline} onChange={e => setTagline(e.target.value)} placeholder="Tagline" className="w-full text-base font-serif text-stone-900 border-b border-stone-200 pb-1.5 mb-3 focus:outline-none focus:border-stone-700 bg-transparent" data-testid="curate-tagline" />
            <textarea value={storyline} onChange={e => setStoryline(e.target.value)} rows={11} placeholder="Editorial storyline. Click 'AI Draft' to auto-compose, then refine here." className="w-full text-sm text-stone-800 leading-relaxed focus:outline-none resize-none font-serif bg-transparent" data-testid="curate-storyline" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-stone-200">
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Best for</span>
                <input value={bestFor} onChange={e => setBestFor(e.target.value)} placeholder="couples, families" className="w-full px-2 py-1 border border-stone-300 rounded text-sm" data-testid="curate-best-for" />
              </label>
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Experience types</span>
                <input value={expTypes} onChange={e => setExpTypes(e.target.value)} placeholder="forest, river" className="w-full px-2 py-1 border border-stone-300 rounded text-sm" data-testid="curate-exp-types" />
              </label>
            </div>
          </section>

          {/* Editorial Tier — INTERNAL classification, not pricing.
              Used by /directory, /valleys, and listing badges to decide where this stay surfaces. */}
          <section className="bg-stone-100 rounded p-4 border border-stone-300" data-testid="curate-editorial-tier">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-[10px] tracking-[0.3em] uppercase text-stone-700">Editorial Tier · internal classification</h3>
              <span className="text-[10px] text-stone-500 italic">Not pricing — your editorial discretion</span>
            </div>
            <div className="grid md:grid-cols-2 gap-3 items-start">
              <label className="text-xs">
                <select
                  value={partnershipStatus}
                  onChange={e => setPartnershipStatus(e.target.value)}
                  className="w-full px-2 py-2 border border-stone-400 rounded text-sm bg-white font-medium"
                  data-testid="curate-tier"
                >
                  <option value="scraped">Scraped — raw, no review</option>
                  <option value="discovered">Discovered — scraped + WhatsApp (booking-ready)</option>
                  <option value="partner">Collective — curated, vetted</option>
                  <option value="valley_partner">Valley Partner — flagship for its valley</option>
                  <option value="featured">Featured — editorial pick of the season</option>
                </select>
              </label>
              <p className="text-[11px] text-stone-600 leading-relaxed">
                <b className="text-stone-800">Quick rule:</b> Scraped → no booking path · Discovered → has WhatsApp · Collective → vetted &amp; storyline written · Valley Partner → one per valley · Featured → top homepage rail.
              </p>
            </div>
          </section>

          {/* Stay Vitals — drives the dramatic strip on the live page */}
          <section className="bg-amber-50/40 rounded p-4 border border-amber-200" data-testid="curate-vitals">
            <h3 className="text-[10px] tracking-[0.3em] uppercase text-amber-900/70 mb-3">Stay Vitals — at-a-glance strip</h3>

            {/* Inventory breakdown — granular per-unit-type counts */}
            <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5">Inventory · how many of each</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
              <InvField label="Rooms" value={invRooms} onChange={setInvRooms} testid="curate-inv-rooms" />
              <InvField label="Cottages" value={invCottages} onChange={setInvCottages} testid="curate-inv-cottages" />
              <InvField label="Family rooms" value={invFamilyRooms} onChange={setInvFamilyRooms} testid="curate-inv-family-rooms" />
              <InvField label="Suites" value={invSuites} onChange={setInvSuites} testid="curate-inv-suites" />
              <InvField label="Tents" value={invTents} onChange={setInvTents} testid="curate-inv-tents" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Sleeps max · total guests</span>
                <input type="number" min="0" value={sleepsMax} onChange={e => setSleepsMax(e.target.value)} placeholder="e.g. 10" className="w-full px-2 py-1 border border-stone-300 rounded text-sm" data-testid="curate-sleeps-max" />
              </label>
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Starting price (₹)</span>
                <input type="number" min="0" value={startingPrice} onChange={e => setStartingPrice(e.target.value)} placeholder="e.g. 5500" className="w-full px-2 py-1 border border-stone-300 rounded text-sm" data-testid="curate-price" />
              </label>
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Price unit</span>
                <select value={priceUnit} onChange={e => setPriceUnit(e.target.value)} className="w-full px-2 py-1 border border-stone-300 rounded text-sm bg-white" data-testid="curate-price-unit">
                  <option value="per_room">Per room / night</option>
                  <option value="per_person">Per person / night</option>
                  <option value="whole_property">Whole property / night</option>
                </select>
              </label>
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Price includes (free text)</span>
                <input value={priceIncludes} onChange={e => setPriceIncludes(e.target.value)} placeholder="with breakfast" className="w-full px-2 py-1 border border-stone-300 rounded text-sm" data-testid="curate-price-includes" />
              </label>
              <label className="text-xs">
                <span className="block text-stone-500 mb-1">Unit type (auto if blank)</span>
                <input value={unitType} onChange={e => setUnitType(e.target.value)} placeholder="cottages / rooms / suites" className="w-full px-2 py-1 border border-stone-300 rounded text-sm" data-testid="curate-unit-type" />
              </label>
            </div>
            <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="curate-includes-breakfast-wrap">
              <input type="checkbox" checked={includesBreakfast} onChange={e => setIncludesBreakfast(e.target.checked)} className="w-4 h-4 rounded border-stone-400 text-amber-600 focus:ring-amber-500" data-testid="curate-includes-breakfast" />
              <span className="text-sm text-stone-800">Includes <b>breakfast</b></span>
            </label>
            <label className="block text-xs mt-3">
              <span className="block text-stone-500 mb-1">Amenities (comma-separated — these become icons)</span>
              <input value={amenities} onChange={e => setAmenities(e.target.value)} placeholder="wood_stove, breakfast, parking, pet_friendly, fishing, trekking" className="w-full px-2 py-1 border border-stone-300 rounded text-sm" data-testid="curate-amenities" />
            </label>
          </section>

          {/* Photos */}
          <section className="bg-stone-50 rounded p-4 border border-stone-200">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h3 className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Photo Library — {libraryAll.length} total · {selectedSet.size} selected</h3>
              <div className="flex items-center gap-1.5">
                <label className="px-2 py-1 text-[10px] bg-stone-900 text-white rounded cursor-pointer hover:bg-stone-700 flex items-center gap-1" data-testid="upload-photo-label" title="Upload images directly from your computer (max 15 MB each)">
                  <Upload size={10} /> Upload
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { onPickFiles(e.target.files); e.target.value = ''; }} data-testid="upload-photo-input" />
                </label>
              </div>
            </div>

            {/* Add by URL — pasted image links (e.g. from host's WhatsApp message, Google Drive share, etc.) */}
            <div className="flex gap-1.5 mb-3">
              <input
                value={addUrlValue}
                onChange={(e) => setAddUrlValue(e.target.value)}
                placeholder="Paste image URL(s) — separate multiple with commas or new lines"
                className="flex-1 px-2 py-1.5 text-[11px] border border-stone-300 rounded font-mono focus:outline-none focus:border-stone-900"
                data-testid="add-photo-url-input"
              />
              <button
                onClick={addPhotoByUrl}
                disabled={busy || !addUrlValue.trim()}
                className="px-2.5 py-1.5 text-[10px] bg-stone-700 text-white rounded hover:bg-stone-900 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="add-photo-url-btn"
              ><LinkIcon size={10} /> Add URL</button>
            </div>
            {libraryAll.length === 0 ? (
              <div className="border-2 border-dashed border-stone-300 rounded p-6 text-center text-xs text-stone-500">
                <ImagePlus size={24} className="mx-auto text-stone-400 mb-2" />
                <div className="font-medium text-stone-700 mb-1">No photos yet</div>
                <div className="text-[11px] leading-relaxed">
                  Three ways to add them:<br />
                  <b>1.</b> Click <b>Upload</b> above to add from your computer · <b>2.</b> Paste image URLs in the box above · <b>3.</b> Add a social link in the right sidebar and click <b>Scrape & Pull</b>
                </div>
              </div>
            ) : (
              <>
                <p className="text-[11px] text-stone-700 mb-3 bg-amber-50 border border-amber-200 rounded p-2 leading-relaxed">
                  <b>How sections work:</b> <b>Drag</b> any photo onto a section below to place it there (also auto-adds it to the live set). Drag back to <b>Unassigned</b> to remove it from the live page. Drop a photo into <b>Hero</b> to make it the top-of-page image.
                </p>
                {(() => {
                  // Group library by current section assignment.
                  const buckets = {};
                  for (const k of SECTION_KEYS) buckets[k] = [];
                  const unassigned = [];
                  for (const p of libraryAll) {
                    if (selectedSet.has(p.url)) {
                      const sec = photoSections[p.url] || 'property';
                      (buckets[sec] || buckets.property).push(p);
                    } else {
                      unassigned.push(p);
                    }
                  }

                  // (When previewMode is on, the right sidebar shows the
                  // full live preview pane — see <LivePreviewPane /> below.
                  // Inline preview removed to avoid duplication.)

                  const assignToSection = (url, sectionKey) => {
                    const nextSel = new Set(selectedSet);
                    nextSel.add(url);
                    setSelectedSet(nextSel);
                    if (sectionKey === 'hero') {
                      // Only one hero allowed — demote any existing hero to 'property'
                      const next = { ...photoSections };
                      for (const [u, s] of Object.entries(next)) if (s === 'hero' && u !== url) next[u] = 'property';
                      next[url] = 'hero';
                      setPhotoSections(next);
                    } else {
                      setPhotoSections({ ...photoSections, [url]: sectionKey });
                    }
                  };
                  const unassignPhoto = (url) => {
                    const nextSel = new Set(selectedSet);
                    nextSel.delete(url);
                    setSelectedSet(nextSel);
                  };

                  return (
                    <div data-testid="section-buckets">
                      {PHOTO_SECTIONS.map(s => (
                        <SectionDropRow
                          key={s.key}
                          sectionKey={s.key}
                          label={s.label}
                          hint={s.hint}
                          photos={buckets[s.key]}
                          onAssign={assignToSection}
                          onUnassign={unassignPhoto}
                        />
                      ))}

                      {/* Unassigned library — drag FROM here, or drop OUT of a section TO here */}
                      <div
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const url = e.dataTransfer.getData('photo/url');
                          if (url && selectedSet.has(url)) unassignPhoto(url);
                        }}
                        className="border-2 border-stone-300 border-dashed rounded-md p-2 mt-3 bg-stone-100"
                        data-testid="section-bucket-unassigned"
                      >
                        <div className="flex items-baseline justify-between mb-1.5">
                          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-700">
                            Unassigned · {unassigned.length}
                          </span>
                          <span className="text-[9px] italic text-stone-500">In library, not in live set yet — drag onto a section above</span>
                        </div>
                        <div className="flex gap-1.5 flex-wrap">
                          {unassigned.map(p => <PhotoTile key={p.url} photo={p} onUnassign={unassignPhoto} />)}
                          {unassigned.length === 0 && <span className="text-[10px] text-stone-400 italic py-2 px-1">All photos placed</span>}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </section>
        </div>

        {/* Right pane — toggles between live preview (side-by-side) and the social-links sidebar */}
        <aside className="space-y-3 xl:sticky xl:top-3 xl:self-start">
          {previewMode ? (
            <LivePreviewPane
              stay={stay}
              libraryAll={libraryAll}
              selectedSet={selectedSet}
              photoSections={photoSections}
              tagline={tagline}
              storyline={storyline}
              onEditSocials={() => setShowSocialsModal(true)}
            />
          ) : (
          <>
          <section className="bg-stone-50 rounded p-4 border border-stone-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Social Links</h3>
              <button onClick={scrapeSocials} disabled={busy} className="px-2 py-1 text-[10px] bg-amber-100 text-amber-800 rounded hover:bg-amber-200 flex items-center gap-1 disabled:opacity-50" data-testid="scrape-socials-btn">
                <RefreshCw size={10} /> Scrape & Pull
              </button>
            </div>
            <div className="space-y-2">
              {SOCIAL_FIELDS.map(({ key, label, icon: Icon }) => {
                const meta = stay.social_meta?.[key];
                return (
                  <div key={key}>
                    <label className="flex items-center gap-1.5 text-[11px] text-stone-700 mb-0.5">
                      <Icon size={11} className="text-stone-500" /> {label}
                      {meta?.fetched && <span className="text-[9px] text-emerald-600">· {meta.image_count || 0} imgs</span>}
                    </label>
                    <input
                      value={socialLinks[key] || ''}
                      onChange={e => setSocialLinks({ ...socialLinks, [key]: e.target.value })}
                      placeholder={`${label} URL`}
                      className="w-full px-2 py-1 border border-stone-300 rounded text-[11px]"
                      data-testid={`social-${key}`}
                    />
                  </div>
                );
              })}
            </div>
          </section>

          <section className="bg-stone-50 rounded p-4 border border-stone-200">
            <h3 className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-2">Submission meta</h3>
            <div className="text-[11px] text-stone-700 space-y-1">
              <div><b>Source:</b> <span className="text-[10px] uppercase tracking-wider text-stone-500">{stay.ingest_source || 'manual'}</span></div>
              {stay.created_at && <div><b>Submitted:</b> {new Date(stay.created_at).toLocaleString()}</div>}
              {stay.updated_at && <div><b>Updated:</b> {new Date(stay.updated_at).toLocaleString()}</div>}
              {whatsappNumber && <a className="text-blue-600 hover:underline block" href={`https://wa.me/${(whatsappNumber || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">Open WhatsApp →</a>}
              {hostEmail && <a className="text-blue-600 hover:underline block" href={`mailto:${hostEmail}`}>Email host →</a>}
            </div>
          </section>
          </>
          )}
        </aside>
      </div>

      {/* Quick-edit socials modal — accessible from inside the preview pane */}
      {showAIChat && (
        <div className="fixed right-4 top-20 bottom-4 w-[420px] bg-white rounded-lg shadow-2xl border-2 border-violet-200 z-40 flex flex-col" data-testid="ai-chat-panel">
          <div className="px-4 py-2 bg-violet-700 text-white flex items-center justify-between rounded-t-md">
            <div className="flex items-center gap-2">
              <Sparkles size={14} />
              <span className="text-[12px] font-semibold">AI Editor · {stay?.display_name?.split(',')[0]}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearAIHistory} className="text-[10px] underline opacity-80 hover:opacity-100" data-testid="ai-clear-btn">Clear</button>
              <button onClick={() => setShowAIChat(false)} className="p-0.5 hover:bg-violet-800 rounded"><X size={14} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-violet-50/30">
            {aiHistory.length === 0 && (
              <div className="text-[11px] text-stone-600 leading-relaxed bg-white p-3 rounded border border-stone-200">
                <b className="text-violet-800">Talk to the AI editor.</b> Examples:
                <ul className="mt-1.5 list-disc list-inside space-y-0.5">
                  <li>&ldquo;Make the storyline shorter and more poetic&rdquo;</li>
                  <li>&ldquo;Drop the marketing tone &mdash; keep it plain&rdquo;</li>
                  <li>&ldquo;The tagline doesn&apos;t mention the river &mdash; fix that&rdquo;</li>
                  <li>&ldquo;Add &lsquo;farm stay&rsquo; to experience types&rdquo;</li>
                </ul>
              </div>
            )}
            {aiHistory.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-[11px] leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user' ? 'bg-violet-700 text-white' : 'bg-white border border-stone-200 text-stone-800'
                }`}>
                  {m.content}
                  {m.role === 'assistant' && m.proposal && Object.keys(m.proposal).length > 0 && (
                    <button
                      onClick={() => applyProposal(m.proposal)}
                      className="mt-2 w-full text-[10px] px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 inline-flex items-center justify-center gap-1"
                      data-testid={`ai-apply-${i}`}
                    >
                      ✓ Apply changes to {Object.keys(m.proposal).join(' · ')}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {aiBusy && (
              <div className="text-[11px] text-stone-500 italic px-1">AI is thinking…</div>
            )}
            {aiError && (
              <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">{aiError}</div>
            )}
          </div>
          <div className="p-2 border-t border-stone-200">
            <div className="flex gap-1.5">
              <textarea
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendAIMessage(); } }}
                placeholder="Ask the AI to refine… (⌘ + Enter to send)"
                rows={2}
                className="flex-1 px-2 py-1.5 text-[12px] border border-stone-300 rounded focus:outline-none focus:border-violet-500 resize-none"
                data-testid="ai-input"
              />
              <button
                onClick={sendAIMessage}
                disabled={aiBusy || !aiInput.trim()}
                className="px-3 bg-violet-700 text-white rounded hover:bg-violet-800 disabled:opacity-50 self-stretch"
                data-testid="ai-send-btn"
              ><Send size={14} /></button>
            </div>
          </div>
        </div>
      )}

      {showSocialsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSocialsModal(false)} data-testid="socials-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-stone-900">Social Links · {stay.display_name}</h3>
              <button onClick={() => setShowSocialsModal(false)} className="text-stone-400 hover:text-stone-700"><X size={16} /></button>
            </div>
            <p className="text-[11px] text-stone-600 mb-3">Paste the host&apos;s social URLs, then click <b>Scrape & Pull</b> to fetch photos directly into the library.</p>
            <div className="space-y-2 mb-3">
              {SOCIAL_FIELDS.map(({ key, label, icon: Icon }) => (
                <div key={key}>
                  <label className="flex items-center gap-1.5 text-[11px] text-stone-700 mb-0.5">
                    <Icon size={11} className="text-stone-500" /> {label}
                  </label>
                  <input
                    value={socialLinks[key] || ''}
                    onChange={e => setSocialLinks({ ...socialLinks, [key]: e.target.value })}
                    placeholder={`${label} URL`}
                    className="w-full px-2 py-1.5 border border-stone-300 rounded text-[12px]"
                    data-testid={`modal-social-${key}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSocialsModal(false)} className="px-3 py-1.5 text-xs border border-stone-300 rounded hover:bg-stone-50">Close</button>
              <button onClick={async () => { await saveDraft(); await scrapeSocials(); }} disabled={busy} className="px-3 py-1.5 text-xs bg-amber-700 text-white rounded hover:bg-amber-800 inline-flex items-center gap-1 disabled:opacity-50" data-testid="modal-save-and-scrape-btn">
                <RefreshCw size={12} /> Save & Scrape
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const InvField = ({ label, value, onChange, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-[10px] tracking-wide uppercase text-stone-500 mb-1">{label}</span>
    <input
      type="number"
      min="0"
      max="50"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder="0"
      className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm text-center bg-white"
    />
  </label>
);
